/**
 * Date e durate, scritte per una persona.
 *
 * Erano **quattro copie** della stessa funzione: `AdminPage` ne esportava una e
 * `ConsolePage`, `SupervisionPage` e `ModerationPage` ne tenevano una propria.
 * La regola del progetto è "due si tollerano, tre si estraggono", e quattro
 * erano un invito a correggerne tre su quattro — che è il modo in cui due
 * schermate finiscono per scrivere la stessa ora in due formati diversi.
 *
 * Sta qui e non in una pagina perché **non è di nessuna pagina**: `NetworkPage`
 * e `PlatformPage` importavano `formatWhen` e `formatDuration` da `AdminPage`,
 * cioè da una schermata, e una schermata non è una libreria — chi la apre per
 * cambiare il registro accessi non si aspetta di rompere il monitoraggio del
 * fornitore.
 *
 * Il fuso arriva **sempre da fuori**, e quasi sempre dall'ente: la console si
 * apre da un telefono che può essere in vacanza altrove, e un turno scritto
 * nell'ora del dispositivo è un turno a cui qualcuno non si presenta
 * (sezione 5.5).
 */

/**
 * Giorno, mese e ora: "12 mar, 21:30".
 *
 * È la forma di quasi tutto — un turno passato, una segnalazione, una riga del
 * registro accessi — perché sono cose che si collocano in un giorno preciso.
 */
export function formatWhen(
  iso: string | null | undefined,
  locale: string | undefined,
  timeZone: string | undefined,
): string {
  return format(iso, locale, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
}

/**
 * Giorno della settimana e ora: "lunedì, 09:00".
 *
 * Serve alle cose **imminenti** — quando riapre il servizio, fino a quando
 * resta reperibile un supervisore — dove "lunedì alle 9" si capisce al volo e
 * "24 mar" chiede un calendario. È l'unica delle quattro copie che aveva
 * davvero opzioni proprie, e restava identica nel nome: una funzione con lo
 * stesso nome e un formato diverso è il genere di differenza che si scopre da
 * uno screenshot di qualcun altro.
 */
export function formatWeekdayTime(
  iso: string | null | undefined,
  locale: string | undefined,
  timeZone: string | undefined,
): string {
  return format(iso, locale, {
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
}

/**
 * L'ora e basta: "23:00".
 *
 * Per ciò che **finisce oggi**, dove il giorno è già noto a chi legge: la fine
 * del proprio turno, che si guarda mentre lo si sta facendo. Ci scriveva il
 * giorno della settimana, e la frase che ne usciva — "in turno fino alle
 * mercoledì 23:00" — non era italiano (`docs/DECISIONI.md`, AQ4).
 */
export function formatTime(
  iso: string | null | undefined,
  locale: string | undefined,
  timeZone: string | undefined,
): string {
  return format(iso, locale, { hour: '2-digit', minute: '2-digit', timeZone });
}

/**
 * Una data illeggibile non è un errore da mostrare: è **niente**.
 *
 * `Intl` solleva su un fuso che non conosce e `new Date` accetta qualunque
 * stringa restituendo `Invalid Date`. Sono due modi diversi di arrivare a
 * scrivere "Invalid Date" accanto al nome di una persona in una schermata di
 * moderazione, ed entrambi qui diventano una stringa vuota.
 */
function format(
  iso: string | null | undefined,
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return '';
  }
}

/**
 * Minuti e secondi: "06:15".
 *
 * Con lo zero davanti, perché queste durate stanno **in colonna** in un elenco
 * di sessioni e in un report, e "6:15" sotto "12:40" si legge male. `—` quando
 * la sessione non è finita: una durata assente non è una durata di zero.
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const total = Math.max(0, Math.round(seconds));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
